import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, Subject } from 'rxjs';

import { WebSocketService } from '@core/websocket/websocket.service';
import { WsProtobufService } from '@core/websocket/ws-protobuf.service';
import { SessionService } from '@core/services/session.service';
import {
  DriverReceiveWorkingShiftEvent,
  DriverStateEvent,
  SessionType,
} from '@core/websocket/websocket.models';
import { EventId, RequestPath, ResponseStatus } from '@core/websocket/websocket.constants';

export type MagnetStep = 'send' | 'pending' | 'approved' | 'rejected';

@Injectable({ providedIn: 'root' })
export class MagnetService {
  private readonly ws = inject(WebSocketService);
  private readonly proto = inject(WsProtobufService);
  private readonly sessionService = inject(SessionService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── State signals ────────────────────────────────────────────────────────

  private readonly _step = signal<MagnetStep>('send');
  readonly step = this._step.asReadonly();

  private readonly _activeRequestId = signal<string | null>(null);

  /**
   * IDs запитів, скасованих у цій сесії.
   * Запобігає повторному застосуванню скасованого запиту при наступному init().
   */
  private readonly _cancelledIds = new Set<string>();

  /**
   * Стріляє лише при схваленні через реальний WS-івент (DriverRequestConfirmed).
   * Не стріляє при ініціалізації з workingShiftData/DriverStateEvent (оновлення сторінки).
   * Використовується ApprovalNotificationService для умовного показу сповіщення.
   */
  private readonly _approvedViaWsEvent$ = new Subject<void>();
  readonly approvedViaWsEvent$ = this._approvedViaWsEvent$.asObservable();

  private readonly _rejectedViaWsEvent$ = new Subject<void>();
  readonly rejectedViaWsEvent$ = this._rejectedViaWsEvent$.asObservable();

  // ─── Constructor — WS event listeners ────────────────────────────────────

  constructor() {
    // DriverRequestConfirmed → 'approved'
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestConfirmed),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (this._step() !== 'pending') return;
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestConfirmedEvent(e.payload);
        if (event.id === this._activeRequestId()) {
          this._step.set('approved');
          this._approvedViaWsEvent$.next();
        }
      });

    // DriverRequestRejected → 'rejected'
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverRequestRejected),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (this._step() !== 'pending') return;
        if (!e.payload?.length) return;
        const event = this.proto.decodeDriverRequestEvent(e.payload);
        if (event.id === this._activeRequestId()) {
          this._step.set('rejected');
          this._rejectedViaWsEvent$.next();
        }
      });

    // DriverStateEvent → ініціалізація якщо сторінка ще не відкривалась
    this.ws.events$
      .pipe(
        filter((e) => e.id === EventId.DriverStateEvent),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        if (this._step() !== 'send') return;
        if (!e.payload?.length) return;
        this.initFromDriverStateEvent(this.proto.decodeDriverStateEvent(e.payload));
      });

    // workingShiftData → запасна ініціалізація якщо DriverStateEvent ще не прийшов.
    // DriverReceiveWorkingShiftEvent надходить надійно після кожного підключення;
    // DriverStateEvent може прийти пізніше або взагалі не прийти до відкриття сторінки.
    toObservable(this.sessionService.workingShiftData)
      .pipe(
        filter((data): data is DriverReceiveWorkingShiftEvent => data !== null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        if (this._step() === 'send') {
          // DriverStateEvent не містить finishedDriverRequests, тому wshData завжди
          // перевіряємо як запасне джерело для виявлення підтвердженого запиту.
          this.initFromWorkingShiftData(data);
        } else if (this._step() === 'approved') {
          // Якщо магніт вже схвалено, перевіряємо чи сесія не завершилась (completed).
          this.checkMagnetCompleted(data);
        }
      });
  }

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Ініціалізація при відкритті сторінки магніту.
   * - 'rejected' — транзієнтний стан: скидається при поверненні на сторінку.
   * - Інші стани зберігаються між навігаціями.
   */
  init(): void {
    if (this._step() === 'rejected') {
      this.resetState();
      return;
    }
    if (this._step() !== 'send') return;

    // DriverStateEvent: перевіряємо активні запити та isSendingAllowed
    const liveState = this.sessionService.lastDriverState();
    if (liveState) {
      this.initFromDriverStateEvent(liveState);
    }

    // wshData: перевіряємо finishedDriverRequests (DriverStateEvent їх не містить).
    // Виконуємо навіть якщо liveState є — якщо після liveState крок ще 'send',
    // значить активних запитів немає, але підтверджений завершений запит може бути.
    if (this._step() === 'send') {
      const wshData = this.sessionService.workingShiftData();
      if (wshData) {
        this.initFromWorkingShiftData(wshData);
      }
    }
  }

  /** Відправити запит на увімкнення магніту */
  async sendRequest(): Promise<void> {
    const wshData = this.sessionService.workingShiftData();
    if (!wshData) return;

    const body = this.proto.encodeCreateDriverRequest(
      wshData.workingShift.id,
      SessionType.EnableSending,
      '',
      new Uint8Array(0),
    );

    const response = await this.ws.sendRequest(RequestPath.CreateDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      throw new Error(`CreateDriverRequest (magnet) failed: ${response.code}`);
    }

    if (response.body?.length) {
      const parsed = this.proto.decodeCreateDriverRequestResponse(response.body);
      this._activeRequestId.set(parsed.id);
    } else {
      console.warn('[Magnet] sendRequest: success response had no body — request ID unknown');
    }

    this._step.set('pending');
  }

  /** Скасувати активний запит (стан 'pending') */
  async cancelRequest(): Promise<void> {
    const requestId = this._activeRequestId();
    if (!requestId) return;

    this._cancelledIds.add(requestId);
    const body = this.proto.encodeCancelDriverRequest(requestId);
    const response = await this.ws.sendRequest(RequestPath.CancelDriverRequest, body);
    if (response.status !== ResponseStatus.Success) {
      this._cancelledIds.delete(requestId);
      throw new Error(`CancelDriverRequest (magnet) failed: ${response.code}`);
    }
    this.resetState();
  }

  resetState(): void {
    this._step.set('send');
    this._activeRequestId.set(null);
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private initFromDriverStateEvent(event: DriverStateEvent): void {
    const req = event.requests.find(
      (r) => r.type === SessionType.EnableSending && !this._cancelledIds.has(r.id),
    );
    if (req) {
      this._activeRequestId.set(req.id);
      this._step.set(req.status === 'confirmed' ? 'approved' : 'pending');
      return;
    }
    if (event.workingShift?.isSendingAllowed) {
      this._step.set('approved');
    }
  }

  /**
   * Викликається при оновленні workingShiftData коли крок вже 'approved'.
   * Скидає стан до 'send' якщо останній EnableSending запит завершився ('completed').
   */
  private checkMagnetCompleted(data: DriverReceiveWorkingShiftEvent): void {
    if (data.workingShift.isSendingAllowed) return;

    const hasActiveReq = data.activeDriverRequests.some(
      (r) => r.type === SessionType.EnableSending,
    );
    if (hasActiveReq) return;

    const finishedReqs = data.finishedDriverRequests.filter(
      (r) => r.type === SessionType.EnableSending,
    );
    if (finishedReqs.length === 0) return;

    const lastReq = finishedReqs.reduce((latest, r) =>
      (r.createdAt?.seconds ?? 0) > (latest.createdAt?.seconds ?? 0) ? r : latest,
    );
    if (lastReq.status === 'completed') {
      this.resetState();
    }
  }

  private initFromWorkingShiftData(data: DriverReceiveWorkingShiftEvent): void {
    if (data.workingShift.isSendingAllowed) {
      this._step.set('approved');
      return;
    }

    // Перевіряємо активні запити
    const activeReq = data.activeDriverRequests.find(
      (r) => r.type === SessionType.EnableSending && !this._cancelledIds.has(r.id),
    );
    if (activeReq) {
      this._activeRequestId.set(activeReq.id);
      this._step.set(activeReq.status === 'confirmed' ? 'approved' : 'pending');
      return;
    }

    // Перевіряємо завершені запити — беремо останній EnableSending
    // confirmed → магніт ще активний; completed → можна відправляти новий запит (send)
    const finishedReqs = data.finishedDriverRequests.filter(
      (r) => r.type === SessionType.EnableSending,
    );
    if (finishedReqs.length > 0) {
      const lastReq = finishedReqs.reduce((latest, r) =>
        (r.createdAt?.seconds ?? 0) > (latest.createdAt?.seconds ?? 0) ? r : latest,
      );
      if (lastReq.status === 'confirmed') {
        this._step.set('approved');
      }
      // 'completed' або інший статус → залишаємо 'send' (можна відправити новий запит)
    }
  }
}
